// src/pages/auth/SignUpPage.tsx
//
// Multi-step sign-up wizard. Drives the public auth flow described in
// bizguard-backend/docs/PLAN_SIGNUP_REDESIGN.md and matches the mockup at
// bizguard-backend/docs/mockups/sign-up-mockup.html (Cool Linen palette).
//
// Steps:
//   1. EMAIL    — collect work email; calls /auth/signup/start, receives verificationId.
//   2. VERIFY   — collect 6-digit code; calls /auth/signup/verify-email, receives signupToken.
//   3. PASSWORD — collect + confirm password; live complexity preview against backend rules.
//   4. PROFILE  — collect name + business name; calls /auth/signup/complete, creates user/org
//                 and authenticates. 2FA is optional — enrolled later from Settings → Security.
//
// Google + Apple OAuth (Phase 3 + 3.5): the email step renders both SSO buttons.
//   - The button is enabled iff its env var is set (build time) AND the SDK loaded.
//   - On success the wizard either jumps to /home (existing user) or to the
//     `oauth_profile` step (new user) where we collect name + business name and
//     POST to /auth/oauth/complete (no password — see PLAN_SIGNUP_REDESIGN.md).
//
// All step transitions live in component state (no router-level steps) to keep the
// wizard atomic — refreshing or navigating away resets the wizard, which is the
// desired behaviour for an unauthenticated flow that holds short-lived secrets in memory.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import { ArrowLeft, ArrowRight, Eye, EyeOff, MailCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { authApi } from '@/services/api';

/** Narrow shape of our backend's JSON error responses (silenceToast endpoints). */
interface BackendErrorData {
  code?: string;
  error?: string;
  reason?: string;
  message?: string;
}

/** Pull `{ status, data }` out of an unknown thrown value, narrowing on AxiosError. */
function getAxiosErrorParts(err: unknown): {
  status: number | undefined;
  data: BackendErrorData | undefined;
} {
  if (err instanceof AxiosError) {
    return {
      status: err.response?.status,
      data: err.response?.data as BackendErrorData | undefined,
    };
  }
  return { status: undefined, data: undefined };
}
import { useAuth } from '@/contexts/AuthContext';
import { useGoogleSignIn, type GoogleSignInError } from '@/hooks/useGoogleSignIn';
import { useAppleSignIn, type AppleSignInError } from '@/hooks/useAppleSignIn';
import zaminoEmblem from '@/assets/zamino-emblem.png';
import { loadGoogleAdsTag } from '@/utils/googleAds';
import { track } from '@/utils/analytics';
import type { OAuthNeedsProfileResponse } from '@/types';

// `oauth_profile` is the OAuth-equivalent of `profile` — same layout, but the
// account-creation call is /auth/oauth/complete instead of /auth/signup/complete.
type Step = 'email' | 'verify' | 'password' | 'profile' | 'oauth_profile';

const emailSchema = z.object({
  email: z.string().trim().toLowerCase().email('Enter a valid email address'),
});
type EmailValues = z.infer<typeof emailSchema>;

const profileSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Tell us your name')
    .max(120, 'Name is too long'),
  organizationName: z
    .string()
    .trim()
    .min(1, 'Add your business name')
    .max(120, 'Business name is too long'),
});
type ProfileValues = z.infer<typeof profileSchema>;

// Mirrors the backend complexity rules in bizguard-backend/src/utils/passwordPolicy.ts.
// We keep these as a flat list of independent predicates so the UI can show live progress.
type RuleId = 'length' | 'lower' | 'upper' | 'numberOrSymbol';
const PASSWORD_RULES: { id: RuleId; label: string; test: (v: string) => boolean }[] = [
  { id: 'length', label: 'At least 12 characters', test: (v) => v.length >= 12 },
  { id: 'lower', label: 'A lowercase letter', test: (v) => /[a-z]/.test(v) },
  { id: 'upper', label: 'An uppercase letter', test: (v) => /[A-Z]/.test(v) },
  {
    id: 'numberOrSymbol',
    label: 'A number or symbol',
    test: (v) => /[0-9]/.test(v) || /[^A-Za-z0-9\s]/.test(v),
  },
];

/**
 * Map every OAuth (Google or Apple) failure to a user-facing toast.
 * Cancellation is silent — that's just the user changing their mind.
 *
 * The backend's collision branch (`collision_with_existing_account`) ships a
 * provider-specific message ("Sign in with Google to continue", "Sign in with
 * your password…") so we surface it verbatim — that's the exact action the
 * user needs to take.
 */
function handleOAuthError(provider: 'google' | 'apple', err: unknown): void {
  const reason = (err as Partial<GoogleSignInError | AppleSignInError> | null)?.reason;
  const providerLabel = provider === 'google' ? 'Google' : 'Apple';

  if (reason === 'cancelled') return;
  if (reason === 'unavailable') {
    toast.error(
      `Couldn't open ${providerLabel} sign-in. Check your popup blocker and try again.`,
    );
    return;
  }
  if (reason === 'not_configured') {
    toast.error(`${providerLabel} sign-in is not configured on this server.`);
    return;
  }

  // Backend-side rejections are AxiosError shapes (silenceToast=true so we own the UX).
  const axiosErr = err as {
    response?: { status?: number; data?: { code?: string; error?: string } };
  } | null;
  const code = axiosErr?.response?.data?.code;
  const serverMessage = axiosErr?.response?.data?.error;

  if (code === 'collision_with_existing_account' && serverMessage) {
    toast.error(serverMessage);
    return;
  }
  if (code === 'google_not_configured' || code === 'apple_not_configured') {
    toast.error(`${providerLabel} sign-in is not configured on this server.`);
    return;
  }
  if (code === 'bad_token' || code === 'missing_email') {
    toast.error(`Couldn't verify your ${providerLabel} account. Please try again.`);
    return;
  }
  toast.error(`${providerLabel} sign-in failed. Please try again.`);
}

/** Maps backend signup-complete error codes to inline error placement. */
function fieldForCompleteError(code: string | undefined): keyof ProfileValues | 'password' | 'general' {
  switch (code) {
    case 'breached':
    case 'complexity':
    case 'invalid_password':
      return 'password';
    case 'invalid_token':
    case 'account_exists':
    default:
      return 'general';
  }
}

/**
 * If the SignInPage handed off a Google identity that needs a profile,
 * the navigation state carries the `oauth_signup` payload. We pluck it
 * once on mount, then nuke history state so a refresh restarts the wizard.
 */
interface SignUpLocationState {
  oauthSignup?: OAuthNeedsProfileResponse;
}

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeSignup, signInWithGoogle, signInWithApple, completeOAuthSignup } = useAuth();
  const google = useGoogleSignIn();
  const apple = useAppleSignIn();

  // Pre-seed from /signin Google handoff if present.
  const initialOAuthHandoff = (location.state as SignUpLocationState | null)?.oauthSignup;

  const [step, setStep] = useState<Step>(
    initialOAuthHandoff ? 'oauth_profile' : 'email',
  );
  const [oauthSignup, setOauthSignup] = useState<OAuthNeedsProfileResponse | null>(
    initialOAuthHandoff ?? null,
  );

  // Auth routes are the only place the app loads an advertising tag — see
  // CAMPAIGN_TRACKING_SPEC.md §6.2.
  useEffect(() => {
    loadGoogleAdsTag();
  }, []);

  // Strip the navigation state on first render so a manual refresh resets the
  // wizard cleanly (history would otherwise replay the OAuth state).
  useEffect(() => {
    if (initialOAuthHandoff) {
      window.history.replaceState({}, document.title);
    }
    // intentional: only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wizard-level state held in memory (intentionally not persisted).
  const [email, setEmail] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [signupToken, setSignupToken] = useState('');
  // Password lives in a ref so it never round-trips through React devtools / serialised
  // state and gets cleared as soon as the wizard finishes.
  const pendingPasswordRef = useRef<string>('');

  /**
   * Click handler for the "Continue with Google" button on the email step.
   * Drives the popup, then either lands the user on /home or swaps the
   * wizard into the `oauth_profile` branch. Errors are surfaced via toast.
   */
  const handleGoogleClick = async () => {
    if (!google.configured) {
      toast('Google sign-in is not configured on this server.');
      return;
    }
    try {
      const idToken = await google.signIn();
      const result = await signInWithGoogle(idToken);
      if (result.kind === 'signed_in') {
        navigate('/home', { replace: true });
        return;
      }
      setOauthSignup(result.payload);
      setStep('oauth_profile');
    } catch (err: unknown) {
      handleOAuthError('google', err);
    }
  };

  /**
   * Same shape as handleGoogleClick. Apple's first-sign-in name payload (if any)
   * is forwarded to the backend; the OAuth profile step pre-fills `name` from
   * the resulting `suggestedName` so the user only confirms.
   */
  const handleAppleClick = async () => {
    if (!apple.configured) {
      toast('Apple sign-in is not configured on this server.');
      return;
    }
    try {
      const { idToken, name } = await apple.signIn();
      const result = await signInWithApple({ idToken, name });
      if (result.kind === 'signed_in') {
        navigate('/home', { replace: true });
        return;
      }
      setOauthSignup(result.payload);
      setStep('oauth_profile');
    } catch (err: unknown) {
      handleOAuthError('apple', err);
    }
  };

  return (
    <div className="auth-surface">
      <div className="auth-bg-glow" aria-hidden="true" />
      <div className="auth-frame">
        <div className="auth-brand">
          <Link to="/" aria-label="Zamino home">
            <img src={zaminoEmblem} alt="Zamino" />
          </Link>
        </div>

        {/* Step indicator. Hidden on the OAuth branch (a single-step express
            flow — no progress to show). */}
        {step !== 'oauth_profile' && (
          <div className="auth-steps" aria-label="Sign-up progress">
            {(['email', 'verify', 'password', 'profile'] as Step[]).map((s, idx) => {
              const order: Step[] = ['email', 'verify', 'password', 'profile'];
              const currentIdx = order.indexOf(step);
              const isActive = s === step;
              const isDone = idx < currentIdx;
              return (
                <span
                  key={s}
                  className={`auth-step-dot ${isActive ? 'is-active' : ''} ${isDone ? 'is-done' : ''}`}
                />
              );
            })}
          </div>
        )}

        {step === 'email' && (
          <EmailStep
            initialEmail={email}
            googleConfigured={google.configured}
            googleReady={google.ready}
            onGoogleClick={handleGoogleClick}
            appleConfigured={apple.configured}
            appleReady={apple.ready}
            onAppleClick={handleAppleClick}
            onContinue={(data) => {
              setEmail(data.email);
              setVerificationId(data.verificationId);
              setStep('verify');
            }}
          />
        )}

        {step === 'verify' && (
          <VerifyStep
            email={email}
            verificationId={verificationId}
            onChangeEmail={() => setStep('email')}
            onResendComplete={(newId) => setVerificationId(newId)}
            onVerified={(token) => {
              setSignupToken(token);
              setStep('password');
            }}
          />
        )}

        {step === 'password' && (
          <PasswordStep
            onBack={() => setStep('verify')}
            onContinue={(password) => {
              // We don't call /signup/complete yet — that happens after the profile
              // step so the user submits everything in one atomic transaction.
              pendingPasswordRef.current = password;
              setStep('profile');
            }}
          />
        )}

        {step === 'profile' && (
          <ProfileStep
            onBack={() => setStep('password')}
            onSubmit={async (values) => {
              const password = pendingPasswordRef.current;
              if (!password || !signupToken) {
                toast.error('Session expired. Please start over.');
                setStep('email');
                return { ok: false };
              }
              try {
                await completeSignup({
                  signupToken,
                  password,
                  name: values.name,
                  organizationName: values.organizationName,
                });
                pendingPasswordRef.current = '';
                navigate('/home', { replace: true });
                return { ok: true };
              } catch (err) {
                const { data } = getAxiosErrorParts(err);
                const code: string | undefined = data?.code || data?.error;
                if (code === 'invalid_token' || code === 'expired') {
                  toast.error('Verification expired. Please verify your email again.');
                  setStep('email');
                  return { ok: false };
                }
                return {
                  ok: false,
                  field: fieldForCompleteError(code),
                  message: data?.message || data?.error || 'Could not create your account.',
                };
              }
            }}
          />
        )}

        {step === 'oauth_profile' && oauthSignup && (
          <OAuthProfileStep
            payload={oauthSignup}
            onSubmit={async (values) => {
              try {
                await completeOAuthSignup({
                  oauthSignupToken: oauthSignup.oauthSignupToken,
                  name: values.name,
                  organizationName: values.organizationName,
                });
                // OAuth users skip MFA — straight into the product.
                navigate('/home', { replace: true });
                return { ok: true };
              } catch (err: unknown) {
                const axiosErr = err as {
                  response?: { status?: number; data?: { code?: string; error?: string; message?: string } };
                } | null;
                const code = axiosErr?.response?.data?.code;
                if (code === 'expired' || code === 'invalid' || code === 'wrong_purpose') {
                  toast.error('Sign-up session expired. Please start again.');
                  setOauthSignup(null);
                  setStep('email');
                  return { ok: false };
                }
                if (code === 'collision_with_email_account') {
                  toast.error(
                    'An account with this email already exists. Sign in with your password to continue.',
                  );
                  setOauthSignup(null);
                  setStep('email');
                  return { ok: false };
                }
                return {
                  ok: false,
                  message:
                    axiosErr?.response?.data?.message ||
                    axiosErr?.response?.data?.error ||
                    'Could not create your account.',
                };
              }
            }}
          />
        )}

        <p
          style={{ marginTop: 40, textAlign: 'center', fontSize: 14, color: 'var(--auth-text-muted)' }}
        >
          Already have an account?{' '}
          <Link to="/login" className="auth-link">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
};

// ---------- Step 1: Email --------------------------------------------------

interface EmailStepProps {
  initialEmail: string;
  /** Whether VITE_GOOGLE_OAUTH_CLIENT_ID was set at build time. */
  googleConfigured: boolean;
  /** Whether GIS finished initialising — when false, the button stays disabled. */
  googleReady: boolean;
  onGoogleClick: () => void;
  /** Whether VITE_APPLE_CLIENT_ID and VITE_APPLE_REDIRECT_URI were set at build time. */
  appleConfigured: boolean;
  /** Whether the Apple JS SDK finished initialising. */
  appleReady: boolean;
  onAppleClick: () => void;
  onContinue: (data: { email: string; verificationId: string }) => void;
}

const EmailStep: React.FC<EmailStepProps> = ({
  initialEmail,
  googleConfigured,
  googleReady,
  onGoogleClick,
  appleConfigured,
  appleReady,
  onAppleClick,
  onContinue,
}) => {
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: initialEmail },
  });

  const onSubmit = async (values: EmailValues) => {
    setServerError(null);
    setSubmitting(true);
    try {
      const res = await authApi.signupStart({ email: values.email });
      track('signup_start');
      onContinue({ email: values.email, verificationId: res.verificationId });
    } catch (err) {
      const { status, data } = getAxiosErrorParts(err);
      const code: string | undefined = data?.code || data?.error;
      if (code === 'account_exists') {
        setServerError('An account with that email already exists. Try logging in instead.');
      } else if (code === 'invalid_email_syntax' || code === 'invalid_email') {
        setServerError("That email doesn't look right. Double-check the spelling.");
      } else if (code === 'no_mx' || code === 'mx_lookup_failed') {
        setServerError(
          "We couldn't reach that email's mail server. Try a different address.",
        );
      } else if (code === 'disposable_email') {
        setServerError('Please use your work email — disposable inboxes are not allowed.');
      } else if (status === 429) {
        setServerError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setServerError(data?.message || data?.error || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <h1 className="auth-headline">
        Create your <span className="accent">Zamino</span> account.
      </h1>
      <p className="auth-sub">Free for 14 days. No credit card required.</p>

      <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 40 }}>
        <div>
          <label className="auth-label" htmlFor="email">
            Work email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            className={`auth-field ${errors.email || serverError ? 'is-invalid' : ''}`}
            {...register('email')}
            onChange={(e) => {
              register('email').onChange(e);
              if (serverError) setServerError(null);
            }}
            autoFocus
          />
          {(errors.email?.message || serverError) && (
            <p className="auth-error-text" role="alert">
              {errors.email?.message || serverError}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="auth-pill auth-pill-primary"
          style={{ marginTop: 16 }}
          disabled={submitting}
        >
          {submitting ? 'Sending code…' : 'Continue with email'}
          {!submitting && <ArrowRight size={14} strokeWidth={2.5} />}
        </button>
      </form>

      <div className="auth-divider" style={{ marginTop: 28 }}>
        or
      </div>

      <div style={{ marginTop: 28 }}>
        <SocialButtons
          googleEnabled={googleConfigured && googleReady}
          onGoogleClick={onGoogleClick}
          appleEnabled={appleConfigured && appleReady}
          onAppleClick={onAppleClick}
        />
      </div>

      <p className="auth-micro" style={{ marginTop: 24, textAlign: 'center' }}>
        By continuing, you agree to Zamino&rsquo;s{' '}
        <a href="https://zamino.com/terms" target="_blank" rel="noreferrer">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="https://zamino.com/privacy" target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
        .
      </p>
    </>
  );
};

// ---------- Step 2: Verify -------------------------------------------------

interface VerifyStepProps {
  email: string;
  verificationId: string;
  onChangeEmail: () => void;
  onResendComplete: (newVerificationId: string) => void;
  onVerified: (signupToken: string) => void;
}

const VerifyStep: React.FC<VerifyStepProps> = ({
  email,
  verificationId,
  onChangeEmail,
  onResendComplete,
  onVerified,
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cooldown timer for the "Resend code" link — backend rate-limits more strictly
  // than this; the client-side cooldown is purely UX.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = window.setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const attemptsRef = useRef(0);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setError(null);
    setSubmitting(true);
    attemptsRef.current += 1;
    try {
      const res = await authApi.signupVerifyEmail({ verificationId, code });
      // Expected drop-off #1 — `attempts` tells us whether the emailed code is
      // hard to receive or hard to type.
      track('signup_verified', { attempts: attemptsRef.current });
      onVerified(res.signupToken);
    } catch (err) {
      const { data } = getAxiosErrorParts(err);
      const reason: string | undefined = data?.code || data?.reason || data?.error;
      if (reason === 'expired') {
        setError('That code expired. Send a new one to continue.');
      } else if (reason === 'exhausted' || reason === 'too_many_attempts') {
        setError('Too many incorrect attempts. Send a new code to continue.');
      } else if (reason === 'invalid_code' || reason === 'mismatch') {
        setError("That code doesn't match. Double-check and try again.");
      } else if (reason === 'not_found') {
        setError('Verification expired. Please request a new code.');
      } else {
        setError(data?.message || 'Could not verify the code. Please try again.');
      }
      setCode('');
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setError(null);
    try {
      const res = await authApi.signupStart({ email });
      onResendComplete(res.verificationId);
      setResendCooldown(60);
      toast.success('We sent you a new code.');
    } catch (err) {
      const { status, data } = getAxiosErrorParts(err);
      if (status === 429) {
        setError('Please wait a moment before requesting another code.');
      } else {
        setError(data?.message || 'Could not resend the code. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <h1 className="auth-headline">
        Check your <span className="accent">inbox.</span>
      </h1>
      <p className="auth-sub">
        We sent a 6-digit code to <strong style={{ color: 'var(--auth-text)' }}>{email}</strong>.
        It expires in 15 minutes.
      </p>

      <form onSubmit={onSubmit} style={{ marginTop: 32 }}>
        <label className="auth-label" htmlFor="code">
          Verification code
        </label>
        <input
          id="code"
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
            if (error) setError(null);
          }}
          placeholder="------"
          className={`auth-otp-input ${error ? 'is-invalid' : ''}`}
          aria-invalid={!!error}
        />
        {error && (
          <p className="auth-error-text" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="auth-pill auth-pill-primary"
          style={{ marginTop: 20 }}
          disabled={code.length !== 6 || submitting}
        >
          {submitting ? 'Verifying…' : 'Verify and continue'}
          {!submitting && <ArrowRight size={14} strokeWidth={2.5} />}
        </button>
      </form>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 20,
          fontSize: 13,
          color: 'var(--auth-text-muted)',
        }}
      >
        <button
          type="button"
          onClick={onChangeEmail}
          className="auth-link-subtle"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          Change email
        </button>
        <button
          type="button"
          onClick={onResend}
          disabled={resendCooldown > 0 || resending}
          className="auth-link-subtle"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: resendCooldown > 0 || resending ? 'not-allowed' : 'pointer',
            padding: 0,
            opacity: resendCooldown > 0 || resending ? 0.5 : 1,
          }}
        >
          {resending
            ? 'Sending…'
            : resendCooldown > 0
              ? `Resend in ${resendCooldown}s`
              : 'Resend code'}
        </button>
      </div>

      <p className="auth-micro" style={{ marginTop: 28, textAlign: 'center' }}>
        <MailCheck size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
        Don&rsquo;t see it? Check your spam folder for an email from Zamino.
      </p>
    </>
  );
};

// ---------- Step 3: Password ----------------------------------------------

interface PasswordStepProps {
  onBack: () => void;
  onContinue: (password: string) => void;
}

const PasswordStep: React.FC<PasswordStepProps> = ({ onBack, onContinue }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ruleResults = useMemo(
    () => PASSWORD_RULES.map((r) => ({ ...r, met: r.test(password) })),
    [password],
  );
  const allRulesMet = ruleResults.every((r) => r.met);
  const matches = password.length > 0 && password === confirm;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!allRulesMet) {
      setError('Your password doesn’t meet all of the requirements.');
      return;
    }
    if (!matches) {
      setError('Passwords don’t match.');
      return;
    }
    setError(null);
    onContinue(password);
  };

  return (
    <>
      <button type="button" onClick={onBack} className="auth-back" aria-label="Go back">
        <ArrowLeft size={14} />
        Back
      </button>

      <h1 className="auth-headline">
        Choose a <span className="accent">password.</span>
      </h1>
      <p className="auth-sub">
        Make it strong — we check it against known data breaches when you continue.
      </p>

      <form onSubmit={onSubmit} style={{ marginTop: 32 }}>
        <div>
          <label className="auth-label" htmlFor="password">
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="At least 12 characters"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(null);
              }}
              className={`auth-field auth-field-with-icon ${error ? 'is-invalid' : ''}`}
              autoFocus
            />
            <button
              type="button"
              className="auth-icon-btn"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <div className="auth-rules">
            {ruleResults.map((r) => (
              <div key={r.id} className={`auth-rule ${r.met ? 'is-met' : ''}`}>
                <span className="auth-rule-dot" aria-hidden="true" />
                <span>{r.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <label className="auth-label" htmlFor="confirm">
            Confirm password
          </label>
          <input
            id="confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Re-enter your password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              if (error) setError(null);
            }}
            className={`auth-field ${
              confirm.length > 0 && !matches ? 'is-invalid' : ''
            }`}
          />
          {confirm.length > 0 && !matches && (
            <p className="auth-error-text" role="alert">
              Passwords don&rsquo;t match.
            </p>
          )}
        </div>

        {error && (
          <p className="auth-error-text" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="auth-pill auth-pill-primary"
          style={{ marginTop: 20 }}
          disabled={!allRulesMet || !matches}
        >
          Continue
          <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </form>
    </>
  );
};

// ---------- Step 4: Profile + complete -----------------------------------

interface ProfileSubmitResult {
  ok: boolean;
  field?: keyof ProfileValues | 'password' | 'general';
  message?: string;
}

interface ProfileStepProps {
  onBack: () => void;
  onSubmit: (values: ProfileValues) => Promise<ProfileSubmitResult>;
}

const ProfileStep: React.FC<ProfileStepProps> = ({ onBack, onSubmit }) => {
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: '', organizationName: '' },
  });

  const submit = async (values: ProfileValues) => {
    setSubmitting(true);
    setServerError(null);
    const result = await onSubmit(values);
    if (!result.ok) {
      if (result.field === 'name' || result.field === 'organizationName') {
        setError(result.field, { message: result.message });
      } else {
        setServerError(result.message || 'Could not create your account.');
      }
    }
    setSubmitting(false);
  };

  return (
    <>
      <button type="button" onClick={onBack} className="auth-back" aria-label="Go back">
        <ArrowLeft size={14} />
        Back
      </button>

      <h1 className="auth-headline">
        Tell us a <span className="accent">bit about you.</span>
      </h1>
      <p className="auth-sub">We&rsquo;ll use this on your dashboard and in reports.</p>

      <form onSubmit={handleSubmit(submit)} style={{ marginTop: 32 }}>
        <div>
          <label className="auth-label" htmlFor="name">
            Your name
          </label>
          <input
            id="name"
            type="text"
            autoComplete="given-name"
            placeholder="Jane Doe"
            className={`auth-field ${errors.name ? 'is-invalid' : ''}`}
            {...register('name')}
            autoFocus
          />
          {errors.name?.message && (
            <p className="auth-error-text" role="alert">
              {errors.name.message}
            </p>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <label className="auth-label" htmlFor="organizationName">
            Business name
          </label>
          <input
            id="organizationName"
            type="text"
            autoComplete="organization"
            placeholder="Acme Co."
            className={`auth-field ${errors.organizationName ? 'is-invalid' : ''}`}
            {...register('organizationName')}
          />
          {errors.organizationName?.message && (
            <p className="auth-error-text" role="alert">
              {errors.organizationName.message}
            </p>
          )}
        </div>

        {serverError && (
          <p className="auth-error-text" role="alert" style={{ marginTop: 12 }}>
            {serverError}
          </p>
        )}

        <button
          type="submit"
          className="auth-pill auth-pill-primary"
          style={{ marginTop: 20 }}
          disabled={submitting}
        >
          {submitting ? 'Creating your account…' : 'Create account'}
          {!submitting && <ArrowRight size={14} strokeWidth={2.5} />}
        </button>
      </form>
    </>
  );
};

// ---------- Social buttons -----------------------------------------------
// Each button is gated on (a) the env var being set (configured) and (b) the
// SDK having loaded (ready). When configured=false we keep the button disabled
// instead of hiding it so the visual layout stays stable across environments.

interface SocialButtonsProps {
  googleEnabled: boolean;
  onGoogleClick: () => void;
  appleEnabled: boolean;
  onAppleClick: () => void;
}

const SocialButtons: React.FC<SocialButtonsProps> = ({
  googleEnabled,
  onGoogleClick,
  appleEnabled,
  onAppleClick,
}) => {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <button
        type="button"
        className="auth-pill auth-pill-google"
        onClick={onGoogleClick}
        disabled={!googleEnabled}
        aria-disabled={!googleEnabled}
        title={googleEnabled ? undefined : 'Google sign-in is loading'}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
          />
          <path
            fill="#34A853"
            d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
          />
          <path
            fill="#FBBC05"
            d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.333z"
          />
          <path
            fill="#EA4335"
            d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.294C4.672 5.166 6.656 3.58 9 3.58z"
          />
        </svg>
        <span>Continue with Google</span>
      </button>
      <button
        type="button"
        className="auth-pill auth-pill-apple"
        onClick={onAppleClick}
        disabled={!appleEnabled}
        aria-disabled={!appleEnabled}
        title={appleEnabled ? undefined : 'Apple sign-in is loading'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M17.05 12.04c-.03-2.84 2.32-4.2 2.43-4.27-1.32-1.94-3.39-2.21-4.12-2.24-1.76-.18-3.43 1.04-4.32 1.04-.9 0-2.27-1.01-3.74-.98-1.92.03-3.69 1.12-4.68 2.84-2 3.46-.51 8.59 1.43 11.4.95 1.38 2.07 2.92 3.55 2.86 1.43-.06 1.97-.92 3.7-.92 1.72 0 2.21.92 3.71.89 1.54-.03 2.51-1.39 3.43-2.78 1.09-1.59 1.53-3.13 1.55-3.21-.03-.01-2.97-1.14-3-4.53M14.32 3.84c.78-.95 1.31-2.27 1.16-3.59-1.13.05-2.5.76-3.31 1.71-.72.84-1.36 2.18-1.19 3.47 1.26.1 2.55-.64 3.34-1.59" />
        </svg>
        <span>Continue with Apple</span>
      </button>
    </div>
  );
};

// ---------- OAuth profile step (express, no password) -------------------
// Renders after a successful Google sign-in *if* the user is brand new.
// Same layout as ProfileStep but pre-fills `name` from Google's display name
// and submits via /auth/oauth/complete instead of /auth/signup/complete.

interface OAuthProfileSubmitResult {
  ok: boolean;
  message?: string;
}

interface OAuthProfileStepProps {
  payload: OAuthNeedsProfileResponse;
  onSubmit: (values: ProfileValues) => Promise<OAuthProfileSubmitResult>;
}

const OAuthProfileStep: React.FC<OAuthProfileStepProps> = ({ payload, onSubmit }) => {
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: payload.suggestedName ?? '',
      organizationName: '',
    },
  });

  const submit = async (values: ProfileValues) => {
    setSubmitting(true);
    setServerError(null);
    const result = await onSubmit(values);
    if (!result.ok && result.message) {
      setServerError(result.message);
    }
    setSubmitting(false);
  };

  return (
    <>
      <h1 className="auth-headline">
        Almost <span className="accent">done.</span>
      </h1>
      <p className="auth-sub">
        Continuing as{' '}
        <strong style={{ color: 'var(--auth-text)' }}>{payload.email}</strong>
        {' '}via Google. We just need your business name.
      </p>

      <form onSubmit={handleSubmit(submit)} style={{ marginTop: 32 }}>
        <div>
          <label className="auth-label" htmlFor="oauth-name">
            Your name
          </label>
          <input
            id="oauth-name"
            type="text"
            autoComplete="given-name"
            placeholder="Jane Doe"
            className={`auth-field ${errors.name ? 'is-invalid' : ''}`}
            {...register('name')}
            autoFocus
          />
          {errors.name?.message && (
            <p className="auth-error-text" role="alert">
              {errors.name.message}
            </p>
          )}
        </div>

        <div style={{ marginTop: 16 }}>
          <label className="auth-label" htmlFor="oauth-org">
            Business name
          </label>
          <input
            id="oauth-org"
            type="text"
            autoComplete="organization"
            placeholder="Acme Co."
            className={`auth-field ${errors.organizationName ? 'is-invalid' : ''}`}
            {...register('organizationName')}
          />
          {errors.organizationName?.message && (
            <p className="auth-error-text" role="alert">
              {errors.organizationName.message}
            </p>
          )}
        </div>

        {serverError && (
          <p className="auth-error-text" role="alert" style={{ marginTop: 12 }}>
            {serverError}
          </p>
        )}

        <button
          type="submit"
          className="auth-pill auth-pill-primary"
          style={{ marginTop: 20 }}
          disabled={submitting}
        >
          {submitting ? 'Creating your account…' : 'Create account'}
          {!submitting && <ArrowRight size={14} strokeWidth={2.5} />}
        </button>

        {/* The OAuth-from-login handoff lands here without passing through the
            email step, so the consent notice must appear on this step too. */}
        <p className="auth-micro" style={{ marginTop: 24, textAlign: 'center' }}>
          By continuing, you agree to Zamino&rsquo;s{' '}
          <a href="https://zamino.com/terms" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="https://zamino.com/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </p>
      </form>
    </>
  );
};

export default SignUpPage;
